import { Box, Text, useToggle } from '@factorialco/deprecated-design-system'
import { CardDetailPageDocument } from 'generated/resources/graphql'

import NotFound from 'modules/core/components/NotFound'
import Sidepanel from 'modules/core/components/Sidepanel'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useQuery } from 'modules/core/hooks/useQuery'
import i18n from 'modules/core/lib/i18n'

import { canCancelCard } from 'modules/banking/lib/cards/canCancelCard'
import { canDeactivateCard } from 'modules/banking/lib/cards/canDeactivateCard'
import { canReactivateCard } from 'modules/banking/lib/cards/canReactivateCard'
import { last4digits } from 'modules/banking/lib/cards/last4digits'
import PageLoading from 'modules/layout/components/Page/Loading'

import { ApprovalActions } from './_ApprovalActions'
import { Content } from './_Content'
import { ApproveModal } from './_Content/_ApproveModal'
import { CancelModal } from './_Content/_CancelModal'
import { DeactivateModal } from './_Content/_DeactivateModal'
import { ReactivateModal } from './_Content/_ReactivateModal'
import { RejectModal } from './_Content/_RejectModal'

type Props = {
  cardId: number
  onClose: () => void
  skipCardControlsCategories?: boolean
  open?: boolean
}
export const CardSidepanel: React.FC<Props> = ({
  cardId,
  onClose,
  open = true,
  skipCardControlsCategories = false,
}) => {
  const { company } = useCurrentSession()
  const { data, error } = useQuery(CardDetailPageDocument, {
    variables: {
      id: Number(cardId),
      companyId: company.id,
    },
    skip: !cardId,
    errorPolicy: 'all',
  })

  const card = data?.banking?.card
  const cardPaymentCategories = data?.banking?.cardPaymentCategories
  const canManageCards = !!data?.expenses?._policies?.cardManageCards?.can
  const [showDeactivateModal, toggleDeactivateModal] = useToggle(false)
  const deactivateOption = {
    title: i18n.t('expenses_v2.cards.actions.deactivate'),
    danger: true,
    onClick: () => toggleDeactivateModal(true)(),
  }

  const [showCancelModal, toggleCancelModal] = useToggle(false)
  const cancelOption = {
    title: i18n.t('expenses_v2.cards.actions.cancel'),
    danger: true,
    onClick: () => toggleCancelModal(true)(),
  }

  const [showReactivateModal, toggleReactivateModal] = useToggle(false)
  const reactivateOption = {
    title: i18n.t('expenses_v2.cards.actions.reactivate'),
    danger: false,
    onClick: () => toggleReactivateModal(true)(),
  }

  const [showRejectModal, toggleRejectModal] = useToggle(false)
  const [showApproveModal, toggleApproveModal] = useToggle(false)

  const options = [
    ...(canDeactivateCard(card) ? [deactivateOption] : []),
    ...(canReactivateCard(card) ? [reactivateOption] : []),
    ...(canCancelCard(card) ? [cancelOption] : []),
  ]

  const SidebarTitle = card && (
    <Box alignItems="center" justifyContent="center" flexDirection="row" flexGrow>
      <Text.Normal weight="semibold">{card.alias || last4digits(card)}</Text.Normal>
    </Box>
  )

  const content = () => {
    if (!data && !error) {
      return <PageLoading />
    }

    if (!card) {
      return <NotFound />
    }

    return (
      <>
        <Content
          card={card}
          cardPaymentCategories={cardPaymentCategories ?? []}
          skipCardControlsCategories={skipCardControlsCategories}
          canManageCards={canManageCards}
        />
        {showDeactivateModal && (
          <DeactivateModal card={card} onClose={toggleDeactivateModal(false)} />
        )}
        {showReactivateModal && (
          <ReactivateModal card={card} onClose={toggleReactivateModal(false)} />
        )}
        {showRejectModal && <RejectModal card={card} onClose={toggleRejectModal(false)} />}
        {showApproveModal && <ApproveModal card={card} onClose={toggleApproveModal(false)} />}
        {showCancelModal && <CancelModal card={card} onClose={toggleCancelModal(false)} />}
      </>
    )
  }

  return (
    <Sidepanel
      overlay
      boxPadding
      width="wideXL"
      title={SidebarTitle}
      open={open}
      onClose={onClose}
      headerBorder
      closeLabel={i18n.t('common.close')}
      options={options.length > 0 ? options : undefined}
      footer={
        card && (
          <ApprovalActions
            card={card}
            toggleRejectModal={toggleRejectModal}
            toggleApproveModal={toggleApproveModal}
          />
        )
      }
    >
      {content()}
    </Sidepanel>
  )
}
